/**
 * ERP-FIN-02 Gate AR-04B: AR Aging Dashboard
 * Route: /ar/aging  (ar.aging.view)
 *
 * Provides:
 *   - As-of-date and LE/customer filters
 *   - Aging bucket summary bar chart (visual)
 *   - Total outstanding reconciliation
 *   - Customer-level drill-down table
 *   - Link to Customer AR 360° workspace per row
 *
 * Aging totals reconcile to total outstanding AR for the same cutoff date,
 * legal entity and currency (enforced server-side).
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, ChevronRight, BarChart3, AlertCircle,
  TrendingUp, DollarSign,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { arStatementApi, formatError } from "@/services/api";

// ── Helpers ────────────────────────────────────────────────────────────────────
function fmt(minor, currency = "INR") {
  if (minor == null) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency", currency, minimumFractionDigits: 0,
  }).format(minor / 100);
}
function pct(part, total) {
  if (!total) return 0;
  return Math.round((part / total) * 100);
}
function today() {
  return new Date().toISOString().slice(0, 10);
}

const BUCKET_COLORS = {
  CURRENT:   "#166534",
  "1_30":    "#0176D3",
  "31_60":   "#D97706",
  "61_90":   "#EA580C",
  OVER_90:   "#DC2626",
};

function BucketBar({ buckets, total }) {
  if (!total || !buckets?.length) return null;
  return (
    <div data-testid="aging-bucket-bar">
      {/* ── Visual bar ─────────────────────────────────────────────────────── */}
      <div style={{ height: 22, borderRadius: 8, overflow: "hidden", display: "flex", marginBottom: 10 }}>
        {buckets.map(b => {
          const w = pct(b.amount_minor, total);
          if (!w) return null;
          const col = BUCKET_COLORS[b.code] || "#6B7280";
          return (
            <div
              key={b.code}
              title={`${b.label}: ${fmt(b.amount_minor)} (${w}%)`}
              style={{ width: `${w}%`, background: col, transition: "width 0.4s" }}
            />
          );
        })}
      </div>

      {/* ── Legend cards ────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {buckets.map(b => {
          const col = BUCKET_COLORS[b.code] || "#6B7280";
          const pctVal = pct(b.amount_minor, total);
          return (
            <div
              key={b.code}
              data-testid={`bucket-card-${b.code}`}
              style={{
                background: "#FFF", border: `1px solid ${col}22`,
                borderLeft: `4px solid ${col}`, borderRadius: 8,
                padding: "10px 14px", minWidth: 120, flex: 1,
              }}
            >
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 3 }}>{b.label}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: col }}>{fmt(b.amount_minor)}</div>
              <div style={{ fontSize: 10, color: "#9CA3AF" }}>{pctVal}% · {b.invoice_count} inv.</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ARAgingDashboard() {
  const navigate  = useNavigate();
  const { user }  = useAuth();

  const hasCap   = (cap) => user?.capabilities?.includes(cap);
  const canView  = hasCap("ar.aging.view");

  const [asOfDate,  setAsOfDate]  = useState(today());
  const [custId,    setCustId]    = useState("");
  const [leId,      setLeId]      = useState("");
  const [page,      setPage]      = useState(1);

  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { as_of_date: asOfDate, page, page_size: 50 };
      if (custId) params.customer_id = custId;
      if (leId)   params.legal_entity_id = leId;
      const res = await arStatementApi.getAging(params);
      setData(res.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [asOfDate, custId, leId, page]);

  useEffect(() => { load(); }, [load]);

  if (!canView) {
    return (
      <div style={{ padding: 40, textAlign: "center" }}>
        <AlertCircle size={32} color="#DC2626" />
        <p style={{ marginTop: 12, color: "#6B7280" }}>You do not have permission to view AR aging.</p>
      </div>
    );
  }

  const d = data;

  return (
    <div data-testid="ar-aging-dashboard" style={{ padding: "24px 28px", fontFamily: "'Inter', sans-serif", background: "#F7F9FC", minHeight: "100vh" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#181818", margin: 0 }}>AR Aging Dashboard</h1>
          <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>Outstanding invoice aging as of selected date</div>
        </div>
        <button
          data-testid="refresh-aging-btn"
          onClick={load}
          disabled={loading}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8, border: "1px solid #E2E8F0", background: "#FFF", cursor: "pointer", fontSize: 13 }}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Filters */}
      <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", padding: "16px 20px", marginBottom: 20, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "flex-end" }}>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>As-of Date *</label>
          <input
            data-testid="aging-as-of-date"
            type="date" value={asOfDate}
            onChange={e => { setAsOfDate(e.target.value); setPage(1); }}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>Customer ID (optional)</label>
          <input
            data-testid="aging-customer-filter"
            type="text" placeholder="Customer ID..."
            value={custId}
            onChange={e => { setCustId(e.target.value); setPage(1); }}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, width: 180 }}
          />
        </div>
        <div>
          <label style={{ fontSize: 11, color: "#6B7280", display: "block", marginBottom: 4 }}>Legal Entity (optional)</label>
          <input
            data-testid="aging-le-filter"
            type="text" placeholder="LE ID..."
            value={leId}
            onChange={e => { setLeId(e.target.value); setPage(1); }}
            style={{ padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 13, width: 180 }}
          />
        </div>
        <button
          data-testid="apply-aging-filter-btn"
          onClick={() => { setPage(1); load(); }}
          style={{ padding: "7px 18px", borderRadius: 8, border: "none", background: "#0176D3", color: "#FFF", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
        >
          Apply
        </button>
      </div>

      {loading && <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>Loading aging data…</div>}

      {!loading && d && (
        <>
          {/* Total Summary */}
          <div style={{ display: "flex", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
            <div data-testid="aging-total-outstanding" style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 20px", minWidth: 160 }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Total Outstanding</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: d.total_outstanding_minor > 0 ? "#DC2626" : "#166534" }}>
                {fmt(d.total_outstanding_minor)}
              </div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>{d.total_invoice_count} invoice{d.total_invoice_count !== 1 ? "s" : ""}</div>
            </div>
            <div style={{ background: "#FFF", border: "1px solid #E2E8F0", borderRadius: 12, padding: "14px 20px", minWidth: 140 }}>
              <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 4 }}>Customers with AR</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: "#181818" }}>{d.total_customers}</div>
              <div style={{ fontSize: 11, color: "#9CA3AF" }}>As of {d.as_of_date}</div>
            </div>
          </div>

          {/* Bucket bar chart */}
          <div style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", padding: "18px 20px", marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#181818", marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={16} /> Aging Breakdown
            </div>
            <BucketBar buckets={d.buckets} total={d.total_outstanding_minor} />
          </div>

          {/* Customer drill-down table */}
          <div data-testid="aging-customers-table" style={{ background: "#FFF", borderRadius: 12, border: "1px solid #E2E8F0", overflow: "hidden" }}>
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, fontSize: 14, color: "#181818" }}>
                Customer Aging ({d.total_customers} total)
              </span>
              <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#6B7280" }}>
                {page > 1 && (
                  <button onClick={() => setPage(p => p - 1)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0", cursor: "pointer", background: "#FFF" }}>← Prev</button>
                )}
                {d.customers?.length === 50 && (
                  <button onClick={() => setPage(p => p + 1)} style={{ padding: "4px 10px", borderRadius: 6, border: "1px solid #E2E8F0", cursor: "pointer", background: "#FFF" }}>Next →</button>
                )}
              </div>
            </div>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr style={{ background: "#F8FAFC" }}>
                    <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", color: "#374151" }}>Customer</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", color: "#374151" }}>Total Outstanding</th>
                    {d.buckets?.map(b => (
                      <th key={b.code} style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", color: BUCKET_COLORS[b.code] || "#374151" }}>
                        {b.label}
                      </th>
                    ))}
                    <th style={{ padding: "10px 14px", textAlign: "center", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", color: "#374151" }}>Inv.</th>
                    <th style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, fontSize: 11, borderBottom: "1px solid #E2E8F0", color: "#374151" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {(!d.customers || d.customers.length === 0) ? (
                    <tr>
                      <td colSpan={(d.buckets?.length || 0) + 4} style={{ padding: "24px", textAlign: "center", color: "#9CA3AF" }}>
                        No outstanding AR found for the selected filters.
                      </td>
                    </tr>
                  ) : d.customers.map((c, i) => (
                    <tr
                      key={c.customer_id}
                      data-testid={`aging-row-${i}`}
                      style={{ borderBottom: "1px solid #F1F5F9", background: i % 2 === 0 ? "#FFF" : "#FAFAFA" }}
                    >
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ fontWeight: 600, color: "#181818" }}>{c.customer_name || c.customer_id}</div>
                        <div style={{ fontSize: 10, color: "#9CA3AF", fontFamily: "monospace" }}>{c.customer_id}</div>
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: "#DC2626" }}>
                        {fmt(c.total_outstanding_minor)}
                      </td>
                      {d.buckets?.map(b => (
                        <td key={b.code} style={{ padding: "10px 14px", textAlign: "right", color: c.buckets?.[b.code] ? (BUCKET_COLORS[b.code] || "#374151") : "#9CA3AF" }}>
                          {c.buckets?.[b.code] ? fmt(c.buckets[b.code]) : "—"}
                        </td>
                      ))}
                      <td style={{ padding: "10px 14px", textAlign: "center", color: "#6B7280" }}>{c.invoice_count}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right" }}>
                        <button
                          data-testid={`view-customer-stmt-btn-${i}`}
                          onClick={() => navigate(`/ar/customers/${c.customer_id}/statement`)}
                          style={{ display: "flex", alignItems: "center", gap: 4, padding: "5px 12px", borderRadius: 6, border: "1px solid #0176D3", background: "#EFF6FF", color: "#0176D3", cursor: "pointer", fontSize: 11, fontWeight: 600, marginLeft: "auto" }}
                        >
                          360° <ChevronRight size={11} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {!loading && !d && (
        <div style={{ textAlign: "center", padding: 40, color: "#6B7280" }}>
          Select a date and click Apply to load aging data.
        </div>
      )}
    </div>
  );
}
